import {v4 as uuidv4} from "uuid";
import {WebSocketClient} from "./WebSocketClient";
import Logger from "../../Logger";
import EventEmitter from "../../EventEmitter";
import {sleep} from "../../utils";
import config from "../../../config";
import ClientService from "../../ClientService";
import {ClientType, ShadowPayload} from "./interfaces";
import {GatewayEvent} from "./GatewayEvent";
import {GatewayError} from "./GatewayError";

const logger = new Logger("TriboGateway");

interface Session {
    userId: string;
    sessionId: string;
    janusSessionId: string;
    janusHandleId: string;
}

export default class ShadowGateway extends EventEmitter {
    client: WebSocketClient;
    disconnected: boolean = true;
    autoReconnect: boolean = true;
    clientType = ClientType.SHADOW_APP
    timeout: number = 15000;
    session: Session | null;
    id: string = uuidv4();
    serverUrl: string;
    private disconnectLock: boolean;
    private reconnecting: boolean;

    constructor(serverUrl: string) {
        super();


        if (!serverUrl) {
            throw Error('No serverUrl provided to TriboClient');
        }

        this.serverUrl = serverUrl;

        this.client = new WebSocketClient({
            serverUrl,
            autoReconnect: false,
        });

        this.client.on('disconnect', this.handleDisconnect.bind(this));
        this.client.on('message', this.handleMessage.bind(this));
        this.client.on('message_sent', (message: any) => {
            this.emit('message_sent', message)
        });
    }

    async handleDisconnect(event: CloseEvent) {
        this.emit('disconnected');
        console.log('Disconnected. Event: ');
        console.log(event);
        this.session = null;

        if (this.autoReconnect && !this.disconnectLock) {
            console.log('reconnecting...');
            await this.reconnect();
        }
    }

    async reconnect() {
        if (this.reconnecting) {
            return;
        }
        this.reconnecting = true;
        await sleep(1000);
        this.reconnecting = false;
        try {
            //@todo must be identify instead if server restarts?
            await this.connect();
        } catch (e) {
            logger.error("Error while reconnecting");
        }

    }

    handleMessage(message: ShadowPayload) {
        this.emit('message', message);
        this.emit(message.type, message);
    }

    async getResponse(body: ShadowPayload, responseType: GatewayEvent): Promise<any> {
        this.client.sendMessage(body);

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                reject({
                    message: `ShadowGateway client: operation timeout (${this.timeout} ms)`,
                    body
                })
            }, this.timeout);

            this.once(responseType, (message: ShadowPayload) => {

                if (message.error) {
                    reject(new GatewayError(message.error));
                } else {
                    resolve(message.data);
                }

            });
        });
    }


    async connect(loginToken?: string) {

        this.disconnectLock = false;
        this.emit('connecting');
        console.log('Connecting to shadow gateway...');
        await this.client.connect();

        let session;
        let users;
        let rooms;

        if (loginToken) {
            ({session, users, rooms = []} = await this.tokenLogin(loginToken));
        } else {

            ({session, users, rooms = []} = await this.identify());
        }

        console.log('Connected to shadow gateway...');

        this.emit('connected');
        this.session = session;
        return {session, users, rooms};
    }

    async tokenLogin(urlToken: string) {
        const payload = await this.createLoginPayload(GatewayEvent.TOKEN_LOGIN, {urlToken});
        const result = await this.getResponse(payload, payload.type);

        const {
            token,
        } = result;
        // this.authService.setAuthToken(token);
        config.apiToken = result.token;

        const idResult = await this.identify();

        return {
            ...result,
            ...idResult,
        }
    }

    async createLoginPayload(type: GatewayEvent, data: any): Promise<ShadowPayload> {

        return {
            type,
            data: {
                ...data,
                clientInfo: {
                    id: this.id,
                    ...await ClientService.getInfo()
                },
            },
        }
    }

    async identify() {
        const token = config.apiToken;

        if (!token) {
            throw new Error("No token found. Can't send an identify request without a token");
        }

        const payload = await this.createLoginPayload(GatewayEvent.IDENTIFY, {
            token,
            clientType: this.clientType
        });

        return this.getResponse(payload, payload.type);
    }

    async disconnect() {
        this.session = null;
        this.disconnectLock = true;
        //@todo check that this works properly in a node env
        return this.client.disconnect();
    }

    hasSession() {
        if (!this.session) {
            return false;
        }
        const {userId, sessionId, janusSessionId, janusHandleId} = this.session;
        return userId && sessionId && janusSessionId && janusHandleId;
    }

    isConnected() {
        return this.client.isConnected() && this.hasSession();
    }


    close() {
        return this.client.close();
    }
}
